import { useCallback, useEffect, useState } from 'react'
import { getServerCapacity } from '@/api/server'
import { usePolling } from '@/hooks/usePolling'
import type { ServerCapacity, ServerMount } from '@/types'
import { formatKstDateTime } from '@/utils/datetime'

function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null) return '-'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`
}

function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const days = Math.floor(total / 86400)
  const hours = Math.floor((total % 86400) / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m ${total % 60}s`
}

function Bar({ value }: { value: number | null }) {
  const pct = value ?? 0
  const color = pct >= 90 ? '#c62828' : pct >= 70 ? '#f57f17' : '#2e7d32'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 8, background: '#e4e7eb', borderRadius: 4, overflow: 'hidden' }}>
        <div
          style={{
            width: value == null ? 0 : `${Math.min(Math.max(pct, 0), 100)}%`,
            height: '100%',
            background: color,
            transition: 'width 0.2s',
          }}
        />
      </div>
      <span style={{ width: 48, textAlign: 'right', fontSize: 13, color: '#444', fontVariantNumeric: 'tabular-nums' }}>
        {value == null ? '-' : `${pct.toFixed(1)}%`}
      </span>
    </div>
  )
}

function MetricBlock({
  title,
  value,
  detail,
  percent,
}: {
  title: string
  value: string
  detail?: string
  percent?: number | null
}) {
  return (
    <div style={cardStyle}>
      <div style={cardTitleStyle}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#222', marginBottom: 8 }}>{value}</div>
      {percent !== undefined && <Bar value={percent} />}
      {detail && <div style={{ marginTop: 8, fontSize: 12, color: '#777' }}>{detail}</div>}
    </div>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '7px 0', borderBottom: '1px solid #eee', fontSize: 13 }}>
      <span style={{ width: 140, flexShrink: 0, color: '#888' }}>{label}</span>
      <span style={{ color: '#222', wordBreak: 'break-all' }}>{value}</span>
    </div>
  )
}

function VolumeTable({
  mounts,
  expanded,
  onToggle,
}: {
  mounts: ServerMount[]
  expanded: boolean
  onToggle: () => void
}) {
  if (mounts.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Raw Mounts</div>
        <p style={{ margin: 0, color: '#aaa', fontSize: 13 }}>No storage mounts detected</p>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ ...cardTitleStyle, marginBottom: 0 }}>Raw Mounts</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: '#888' }}>{mounts.length} mount{mounts.length === 1 ? '' : 's'}</span>
          <button type="button" onClick={onToggle} style={toggleBtnStyle}>
            {expanded ? 'Hide' : 'Show'}
          </button>
        </div>
      </div>
      {expanded && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 760 }}>
            <thead>
              <tr>
                {['Mount', 'Device', 'Type', 'Free', 'Total', 'Used'].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {mounts.map((mount) => (
                <tr key={`${mount.device}:${mount.mount_point}`}>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }}>
                      {mount.mount_point}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ color: '#666', wordBreak: 'break-all' }}>{mount.device}</span>
                  </td>
                  <td style={tdStyle}>{mount.fs_type}</td>
                  <td style={tdStyle}>{formatBytes(mount.free_bytes)}</td>
                  <td style={tdStyle}>{formatBytes(mount.total_bytes)}</td>
                  <td style={tdStyle}>
                    <Bar value={mount.used_percent} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function ServerCapacityPage() {
  const [capacity, setCapacity] = useState<ServerCapacity | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showRawMounts, setShowRawMounts] = useState(false)

  const fetchCapacity = useCallback(async () => {
    try {
      const data = await getServerCapacity()
      setCapacity(data)
      setError(null)
    } catch (e: unknown) {
      const err = e as { message?: string }
      setError(err?.message ?? 'Failed to load server capacity.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCapacity()
  }, [fetchCapacity])

  usePolling(fetchCapacity, 15000, !loading)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Server</h1>
        <span style={{ fontSize: 12, color: '#888' }}>Backend host capacity</span>
      </div>

      {loading && <p style={{ color: '#888' }}>Loading server capacity...</p>}
      {error && !loading && (
        <div style={errBannerStyle}>
          {error}
          <button onClick={fetchCapacity} style={{ marginLeft: 12, fontSize: 12, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      )}

      {capacity && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
            <MetricBlock
              title="Disk Available"
              value={formatBytes(capacity.disk_free_bytes)}
              detail={`${formatBytes(capacity.disk_used_bytes)} used of ${formatBytes(capacity.disk_total_bytes)} on ${capacity.disk_path}`}
              percent={capacity.disk_used_percent}
            />
            <MetricBlock
              title="Memory Available"
              value={formatBytes(capacity.memory_available_bytes)}
              detail={
                capacity.memory_total_bytes == null
                  ? 'Memory details are unavailable on this host'
                  : `${formatBytes(capacity.memory_total_bytes - (capacity.memory_available_bytes ?? 0))} used of ${formatBytes(capacity.memory_total_bytes)}`
              }
              percent={capacity.memory_used_percent}
            />
            <MetricBlock
              title="CPU Load"
              value={capacity.load_average_1m == null ? '-' : capacity.load_average_1m.toFixed(2)}
              detail={`5m ${capacity.load_average_5m ?? '-'} / 15m ${capacity.load_average_15m ?? '-'} / ${capacity.cpu_count ?? '-'} cores`}
            />
            <MetricBlock
              title="API Uptime"
              value={formatDuration(capacity.process_uptime_seconds)}
              detail={`Updated ${formatKstDateTime(capacity.collected_at)}`}
            />
          </div>

          <div style={cardStyle}>
            <div style={cardTitleStyle}>Runtime</div>
            <InfoRow label="Hostname" value={capacity.hostname} />
            <InfoRow label="Platform" value={capacity.platform} />
            <InfoRow label="Python" value={capacity.python_version} />
            <InfoRow label="CPU cores" value={String(capacity.cpu_count ?? '-')} />
          </div>

          <VolumeTable
            mounts={capacity.mounts}
            expanded={showRawMounts}
            onToggle={() => setShowRawMounts((current) => !current)}
          />
        </div>
      )}
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '16px 18px',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#666',
  marginBottom: 10,
  textTransform: 'uppercase',
  letterSpacing: 0,
}

const errBannerStyle: React.CSSProperties = {
  color: '#c62828',
  background: '#fdecea',
  padding: '10px 14px',
  borderRadius: 4,
  marginBottom: 16,
}

const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#777',
  background: '#f8f8f8',
  borderBottom: '1px solid #e5e5e5',
}

const tdStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid #eee',
  color: '#333',
  verticalAlign: 'middle',
}

const toggleBtnStyle: React.CSSProperties = {
  border: '1px solid #cfd4dc',
  borderRadius: 4,
  background: '#fff',
  color: '#3949ab',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  padding: '4px 10px',
}
